/**
 * AI Token Display Component
 * Shows token usage and cost breakdown
 */

import { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

import { AiStepResult, AiTokenUsage } from '@/types/ai';
import { formatCost } from '@/services/ai';
import { Button } from '@/components/ui/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

const EMPTY_STEP_RESULTS: AiStepResult[] = [];

interface AiTokenDisplayProps {
  tokenUsage: AiTokenUsage;
  stepResults?: AiStepResult[];
  className?: string;
}

export function AiTokenDisplay({
  tokenUsage,
  stepResults = EMPTY_STEP_RESULTS,
  className,
}: AiTokenDisplayProps) {
  const [isOpen, setIsOpen] = useState(false);

  const hasTokens = tokenUsage.totalTokens > 0;

  if (!hasTokens) {
    return null;
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <span>
            Tokens: {tokenUsage.promptTokens.toLocaleString()} in /{' '}
            {tokenUsage.completionTokens.toLocaleString()} out
          </span>
          <span className="text-foreground">
            | Est. cost: {formatCost(tokenUsage.estimatedCost)}
          </span>
        </div>

        {stepResults.length > 0 && (
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
              {isOpen ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <span className="sr-only">Toggle token breakdown</span>
            </Button>
          </CollapsibleTrigger>
        )}
      </div>

      <CollapsibleContent className="mt-2">
        <div className="rounded-md border border-border bg-muted/50 p-3">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pb-2 font-medium">Step</th>
                <th className="pb-2 text-right font-medium">In</th>
                <th className="pb-2 text-right font-medium">Out</th>
                <th className="pb-2 text-right font-medium">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {stepResults.map((result) => (
                <tr key={result.step}>
                  <td className="py-1.5 capitalize">{result.step}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {result.tokens.prompt.toLocaleString()}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">
                    {result.tokens.completion.toLocaleString()}
                  </td>
                  <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                    {(result.durationMs / 1000).toFixed(1)}s
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-border font-medium">
                <td className="pt-2">Total</td>
                <td className="pt-2 text-right tabular-nums">
                  {tokenUsage.promptTokens.toLocaleString()}
                </td>
                <td className="pt-2 text-right tabular-nums">
                  {tokenUsage.completionTokens.toLocaleString()}
                </td>
                <td className="pt-2 text-right tabular-nums">
                  {formatCost(tokenUsage.estimatedCost)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
